import { useState } from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/cms/ui/alert-dialog";
import { ArchiveIcon, GlobeIcon, WarningOctagonIcon } from "@phosphor-icons/react";
import LureoButton from '@/common/LureoButton';

export type PublishingWarningType =
  | 'archive-will-unpublish-project'
  | 'publish-will-make-project-live'
  | 'unpublish-will-make-project-draft'
  | 'publish-will-make-project-live-from-draft'
  | 'unpublish-will-kill-link'
  | 'archive-will-kill-link'
  | 'revoke-link'
  | 'swap-link';

interface PublishingWarningDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  onCancel: () => void;
  warningType: PublishingWarningType;
  projectName?: string;
  storyName?: string;
  hasUnsavedChanges?: boolean;
  /** The story's public link, when the action takes its live version away (share links 3.12). */
  liveLinkUrl?: string | null;
  killsLink?: boolean;
}

function linkSentence(liveLinkUrl: string | null | undefined, killsLink: boolean, permanent: boolean): string {
  if (!liveLinkUrl && !killsLink) return '';
  const link = liveLinkUrl ? `Its public link ${liveLinkUrl}` : 'Its public link';
  return permanent
    ? ` ${link} and any Spectacles code will stop working for good, on every copy sent or printed. If you publish this story again it gets a new link.`
    : ` ${link} will stop working for everyone who has it, on every copy sent or printed, until a new version is published.`;
}

export function PublishingWarningDialog({
  open,
  onOpenChange,
  onConfirm,
  onCancel,
  warningType,
  projectName = "this project",
  storyName = "this story",
  hasUnsavedChanges = false,
  liveLinkUrl,
  killsLink = false
}: PublishingWarningDialogProps) {
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await onConfirm();
    } finally {
      setIsLoading(false);
      onOpenChange(false);
    }
  };

  const handleCancel = () => {
    onCancel();
    onOpenChange(false);
  };

  const getWarningContent = () => {
    switch (warningType) {
      case 'archive-will-unpublish-project':
        return {
          title: "Archive Story?",
          icon: <ArchiveIcon className="w-6 h-6 text-lureo-warning-ink" />,
          message: `Archiving "${storyName}" takes the last live story in ${projectName} away, so the project will no longer be live for your audience.${linkSentence(liveLinkUrl, killsLink, true)}`,
          confirmText: "Archive Story",
          confirmVariant: "danger" as const
        };
      case 'archive-will-kill-link':
        return {
          title: "Archive Story?",
          icon: <ArchiveIcon className="w-6 h-6 text-lureo-warning-ink" />,
          message: `Archiving "${storyName}" takes its live version away from your audience.${linkSentence(liveLinkUrl, killsLink, true)}`,
          confirmText: "Archive Story",
          confirmVariant: "danger" as const
        };
      case 'revoke-link':
        return {
          title: "Revoke public link?",
          icon: <WarningOctagonIcon className="w-6 h-6 text-lureo-warning-ink" />,
          message: `Every copy of ${liveLinkUrl ?? 'the link'} stops working immediately, for everyone who has it. The story stays live; you can create a new link afterwards.`,
          confirmText: "Revoke link",
          confirmVariant: "danger" as const
        };
      case 'swap-link':
        return {
          title: "Are you sure?",
          icon: <WarningOctagonIcon className="w-6 h-6 text-lureo-warning-ink" />,
          message: "Anybody with the original link will not be able to open this version. A new preview link replaces it. The Spectacles code stays the same.",
          confirmText: "Swap link",
          confirmVariant: "danger" as const
        };
      case 'unpublish-will-kill-link':
        return {
          title: "Unpublish Story?",
          icon: <ArchiveIcon className="w-6 h-6 text-lureo-warning-ink" />,
          message: `Unpublishing "${storyName}" takes its live version away from your audience.${linkSentence(liveLinkUrl, killsLink, false)}`,
          confirmText: "Unpublish Story",
          confirmVariant: "danger" as const
        };
      case 'publish-will-make-project-live':
        return {
          title: "Publish Story?",
          icon: <GlobeIcon className="w-6 h-6 text-lureo-sky-ink" />,
          message: `Publishing "${storyName}" will change the status of ${projectName} to live once it has been approved. This means it will be viewable to the public.`,
          confirmText: hasUnsavedChanges ? "Save Changes & Publish" : "Publish Story",
          confirmVariant: "secondary" as const
        };
      case 'unpublish-will-make-project-draft':
        return {
          title: "Unpublish Story?",
          icon: <ArchiveIcon className="w-6 h-6 text-lureo-warning-ink" />,
          message: `Unpublishing "${storyName}" will change the status of ${projectName} to draft. This means it will no longer be viewable to your audience.${linkSentence(liveLinkUrl, killsLink, false)}`,
          confirmText: "Unpublish Story",
          confirmVariant: "danger" as const
        };
      case 'publish-will-make-project-live-from-draft':
        return {
          title: "Publish Story?",
          icon: <GlobeIcon className="w-6 h-6 text-lureo-sky-ink" />,
          message: `Publishing "${storyName}" will change the status of ${projectName} to live once it has been approved. This means it will be viewable to the public.`,
          confirmText: hasUnsavedChanges ? "Save Changes & Publish" : "Publish Story",
          confirmVariant: "secondary" as const
        };
      default:
        return {
          title: "Confirm Action",
          icon: <WarningOctagonIcon className="w-6 h-6 text-lureo-warning-ink" />,
          message: "Are you sure you want to proceed?",
          confirmText: "Confirm",
          confirmVariant: "secondary" as const
        };
    }
  };

  const content = getWarningContent();

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="cursor-default select-none">
        <AlertDialogHeader>
          <div className="flex items-center gap-3">
            {content.icon}
            <AlertDialogTitle>{content.title}</AlertDialogTitle>
          </div>
          <AlertDialogDescription>
            {content.message}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter>
          <LureoButton
            variant="outline-2"
            suppressTooltip
            className="h-9 px-5 rounded-lureo-radius-button-pill"
            onClick={handleCancel}
            disabled={isLoading}
          >
            Cancel
          </LureoButton>
          <LureoButton
            variant={content.confirmVariant}
            suppressTooltip
            onClick={handleConfirm}
            className="h-9 px-4 rounded-lureo-radius-button-pill"
            disabled={isLoading}
          >
            {isLoading ? "Processing..." : content.confirmText}
          </LureoButton>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}